import { useEffect, useState } from 'react';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  deleteDoc,
  query,
  where,
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { logFavoriteAdd, logFavoriteRemove } from '../utils/hairstylistAnalytics';
import { DEFAULT_PROVIDER_TYPE } from '../config/providerTypes';

export function useFavorites() {
  const { currentUser } = useAuth();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!currentUser) {
      setFavorites([]);
      return;
    }

    const fetchFavorites = async () => {
      setLoading(true);
      try {
        const q = query(collection(db, 'favorites'), where('userId', '==', currentUser.uid));
        const snapshot = await getDocs(q);
        const favoriteIds = snapshot.docs.map((doc) => doc.data().hairstylistId);
        setFavorites(favoriteIds);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching favorites:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchFavorites();
  }, [currentUser]);

  const toggleFavorite = async (hairstylistId) => {
    if (!currentUser) {
      throw new Error('Vous devez être connecté pour ajouter aux favoris');
    }

    try {
      const favoriteRef = doc(db, 'favorites', `${currentUser.uid}_${hairstylistId}`);
      const favoriteDoc = await getDoc(favoriteRef);

      if (favoriteDoc.exists()) {
        await deleteDoc(favoriteRef);
        setFavorites((prev) => prev.filter((id) => id !== hairstylistId));
        
        // Log favorite remove event
        logFavoriteRemove(hairstylistId, currentUser.uid);
      } else {
        await setDoc(favoriteRef, {
          userId: currentUser.uid,
          hairstylistId,
          providerType: DEFAULT_PROVIDER_TYPE, // Type de prestataire (par défaut 'hair')
          createdAt: new Date(),
        });
        setFavorites((prev) => [...prev, hairstylistId]);
        
        // Log favorite add event
        logFavoriteAdd(hairstylistId, currentUser.uid);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error toggling favorite:', error);
      throw error;
    }
  };

  const isFavorite = (hairstylistId) => {
    return favorites.includes(hairstylistId);
  };

  return { favorites, loading, toggleFavorite, isFavorite };
}

